import React from "react";
import { OwcButton, OwcIconButton, OwcExpandable } from "@one/react";
import { ConfirmDialogReason } from "../../../../components/shared/ConfirmDialogReason";
import CreateFieldStyled from "../CreateFieldStyled";
import CustomList from "../../../../components/shared/List";
import { createField } from "../../components/tableMetaHelpers";

const SectionContainer = ({ children }) => {
  return (
    <div style={{ marginTop: 8, border: "1px solid #efefef", borderRadius: 4, overflow: "hidden" }}>
      {children}
    </div>
  );
};

const equipmentMeta = {
  fields: {
    equipmentName: createField("Equipment", "0 1 20%"),
    membershipType: createField("Membership type", "0 1 16%"),
    cidmGroup: createField("CIDM Group", "0 1 20%"),
    users: createField("Users", "0 1 22%"),
    bookingGroup: createField("Booking group", "0 1 22%"),
  },
};

const modelLocationMeta = {
  fields: {
    equipmentModel: createField("Model", "0 1 16%"),
    location: createField("Location", "0 1 16%"),
    membershipType: createField("Membership type", "0 1 14%"),
    cidmGroup: createField("CIDM Group", "0 1 16%"),
    users: createField("Users", "0 1 18%"),
    bookingGroup: createField("Booking group", "0 1 20%"),
  },
};

const viewsMeta = {
  fields: {
    assignedGroups: createField("Assigned groups", "0 1 22%"),
    filterName: createField("Filter name", "0 1 18%"),
    columnSet: createField("Columns set", "0 1 18%"),
    createdBy: createField("Created by", "0 1 18%"),
    modifiedBy: createField("Modified by", "0 1 18%"),
  },
};

const DeleteGroupDialog = ({ open, onCancel, deleteGroup, loading, usageInfo, bookingGroup }) => {
  const equipmentRows = usageInfo?.equipmentRows || [];
  const modelLocationRows = usageInfo?.modelLocationRows || [];
  const viewRows = usageInfo?.viewRows || [];
  const ruleCount = equipmentRows.length + modelLocationRows.length;
  const viewCount = usageInfo?.viewCount || 0;
  const hasUsage = ruleCount > 0 || viewCount > 0;
  const rulesText = ruleCount === 1 ? "rule" : "rules";
  const viewsText = viewCount === 1 ? "view" : "views";

  return (
    <ConfirmDialogReason
      open={open}
      close={onCancel}
      size="lg"
      approveText={"Delete"}
      approveColor="primary"
      approveVariant="primary"
      cancelText="Cancel"
      cancelVariant="outlined"
      cancelColor="primary"
      onCancel={onCancel}
      testid={"delete-group-dialog"}
      title={"Delete booking group"}
      renderActions={({ cancelText, approveText }) => (
        <div style={{ paddingBottom: 8, display: "flex" }}>
          {hasUsage ? (
            <OwcButton data-testid="confirm-dialog-actions-button-close" onClick={onCancel} variant="primary">
              Close
            </OwcButton>
          ) : (
            <>
              <OwcButton
                data-testid="confirm-dialog-actions-button-cancel"
                onClick={onCancel}
                variant="secondary"
                style={{ marginRight: 32 }}
              >
                {cancelText}
              </OwcButton>
              <OwcButton
                data-testid="confirm-dialog-actions-button-approve"
                onClick={deleteGroup}
                variant="primary"
                disabled={loading}
              >
                {approveText}
              </OwcButton>
            </>
          )}
        </div>
      )}
    >
      <CreateFieldStyled>{`Are you sure you want to delete this booking group${bookingGroup ? `: ${bookingGroup}` : ""}?`}</CreateFieldStyled>
      {hasUsage ? (
        <>
          <CreateFieldStyled>
            <strong>Caution:</strong>
            {` This group is used by ${ruleCount} ${rulesText} and ${viewCount} ${viewsText}, click on the `}
            <span
              style={{
                margin: "0 4px",
                display: "inline-flex",
                verticalAlign: "baseline",
                position: "relative",
                top: "-8px",
              }}
            >
              <OwcIconButton
                icon="search"
                type="legacy"
                flat
                disabled
                data-testid="delete-group-dialog-search-icon"
                style={{ lineHeight: 1 }}
              />
            </span>
            {" icon next to group to see all linked rules."}
          </CreateFieldStyled>

          <CreateFieldStyled>The following rules and views currently reference this group:</CreateFieldStyled>

          <OwcExpandable variant="standard" round style={{ marginTop: 8 }}>
            <span slot="title">Equipment rules ({equipmentRows.length})</span>
            <span slot="content" style={{ display: "block" }}>
              <SectionContainer>
                <CustomList
                  meta={equipmentMeta}
                  data={equipmentRows}
                  fieldArray={[]}
                  onRequestSort={false}
                  isReverseOrder={true}
                  orderBy="equipmentName"
                  loading={false}
                />
              </SectionContainer>
            </span>
          </OwcExpandable>

          <OwcExpandable variant="standard" round style={{ marginTop: 8 }}>
            <span slot="title">Model and location rules ({modelLocationRows.length})</span>
            <span slot="content" style={{ display: "block" }}>
              <SectionContainer>
                <CustomList
                  meta={modelLocationMeta}
                  data={modelLocationRows}
                  fieldArray={[]}
                  onRequestSort={false}
                  isReverseOrder={true}
                  orderBy="equipmentModel"
                  loading={false}
                />
              </SectionContainer>
            </span>
          </OwcExpandable>

          <OwcExpandable variant="standard" round style={{ marginTop: 8 }}>
            <span slot="title">Views ({viewRows.length})</span>
            <span slot="content" style={{ display: "block" }}>
              <SectionContainer>
                <CustomList
                  meta={viewsMeta}
                  data={viewRows}
                  fieldArray={[]}
                  onRequestSort={false}
                  isReverseOrder={true}
                  orderBy="assignedGroups"
                  loading={false}
                />
              </SectionContainer>
            </span>
          </OwcExpandable>
        </>
      ) : (
        <CreateFieldStyled>Note: this group is not used by rules or views</CreateFieldStyled>
      )}
    </ConfirmDialogReason>
  );
};

export default DeleteGroupDialog;
